import * as React from 'react';
import {useSelector} from 'react-redux';

import {Field, formValueSelector} from 'redux-form';

import {SaveButton} from 'shells/button/save-button/save-button';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {validateHttpsUrl} from 'shells/form/validation/https-url';
import {nonNull} from 'shells/form/validation/non-null';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {ReduxFormRadioGroup} from 'shells/redux-form-fields/radio-group';
import {ReduxFormSelectPickerGeneric} from 'shells/redux-form-fields/select-picker-generic/select-picker-generic';
import {ReduxFormSelectPickerMulti} from 'shells/redux-form-fields/select-picker-generic/select-picker-multi';
import {ReduxFormTextField} from 'shells/redux-form-fields/textfield';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {Body} from 'shells/typography/body';
import {Header} from 'shells/typography/header';

import type {EnrollmentFormFieldKey} from 'nutshell-graphql-types';
import type {SubmitInboxEnrollmentForm} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../../../store';

import {useUpdateEnrollmentForm} from '../../graphql/hooks';

import {
    BUSINESS_REGIONS_OF_OPERATION,
    BUSINESS_TYPES,
    COUNTRIES,
    GOVERNMENT_REGISTRATION_TYPES,
    INDUSTRIES,
    POSITIONS,
    REGIONS,
    YES_NO_OPTIONS,
} from './consts';
import {getGovernmentRegistrationTypeTitle} from './helpers';
import {OwnershipTypeField} from './ownership-type-field';

import styles from './enrollment-form.module.css';

// Our textfield height should match that of SelectPickerGeneric, but by defualt
// that's 31px, when we want 34px.
export const TEXTFIELD_HEIGHT = 34;

// for dynamic business registration number header
const formSelector = formValueSelector('sms_enrollment_form');
type Props = {
    handleSubmit: (eventOrSubmit?: any) => Promise<any>;
    submitting: boolean;
    invalid: boolean;
    isLoading: boolean;
    banner: React.ReactNode | null | undefined;
    isDisabled: boolean;
};

type FieldError = {
    key: EnrollmentFormFieldKey;
    error: string | null | undefined;
};

export function Form(props: Props) {
    const [isSavingKey, setIsSavingKey] = React.useState<EnrollmentFormFieldKey | null | undefined>(
        null
    );
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = React.useState<FieldError[]>([]);
    const [submitErrorMessage, setSubmitErrorMessage] = React.useState<string | null>(null);

    const {updateEnrollmentForm} = useUpdateEnrollmentForm();
    const registrationType = useSelector((state: NutshellState) =>
        formSelector(state, 'GOVERNMENT_REGISTRATION_TYPE')
    );
    const privacyPolicyUrlValue = useSelector((state: NutshellState) =>
        formSelector(state, 'PRIVACY_POLICY_URL')
    );
    const termsAndConditionsUrlValue = useSelector((state: NutshellState) =>
        formSelector(state, 'TERMS_AND_CONDITIONS_URL')
    );

    const setErrorForField = (fieldError: FieldError) => {
        setFieldErrors((prevFieldErrors) => {
            const newFieldErrors = prevFieldErrors.filter((error) => error.key !== fieldError.key);

            return [...newFieldErrors, fieldError];
        });
    };

    const getErrorMessageForField = (key: EnrollmentFormFieldKey): string | null | undefined => {
        const fieldError = fieldErrors.find((error) => error.key === key);

        return fieldError ? fieldError.error : null;
    };

    const clearSaveState = () => {
        setIsSaving(false);

        setTimeout(() => {
            setIsSavingKey(null);
        }, 1000);
    };

    const handleSubmitErrors = (data: SubmitInboxEnrollmentForm): boolean => {
        const values = data.inboxEnrollmentFormSubmit
            ? data.inboxEnrollmentFormSubmit.enrollmentForm.values
            : [];
        let hasErrors = false;

        values.forEach((value) => {
            if (value.error) {
                hasErrors = true;
                setErrorForField({key: value.name, error: value.error});
            }
        });

        return hasErrors;
    };

    const setHttpsValidationFieldErrors = () => {
        const privacyPolicyUrlError = validateHttpsUrl(privacyPolicyUrlValue || '');
        const termsAndConditionsUrlError = validateHttpsUrl(termsAndConditionsUrlValue || '');

        if (privacyPolicyUrlError) {
            setErrorForField({
                key: 'PRIVACY_POLICY_URL',
                error: privacyPolicyUrlError,
            });
        }

        if (termsAndConditionsUrlError) {
            setErrorForField({
                key: 'TERMS_AND_CONDITIONS_URL',
                error: termsAndConditionsUrlError,
            });
        }
    };

    const handleSave = (key: EnrollmentFormFieldKey, value: string) => {
        setIsSavingKey(key);
        setIsSaving(true);
        setErrorForField({key, error: null});

        updateEnrollmentForm({key, value})
            .then((res) => {
                const error =
                    res &&
                    res.data &&
                    res.data.inboxEnrollmentFormUpdate &&
                    res.data.inboxEnrollmentFormUpdate.enrollmentFormField &&
                    res.data.inboxEnrollmentFormUpdate.enrollmentFormField.error;

                if (error) {
                    setErrorForField({key, error});
                }

                clearSaveState();
            })
            .catch((e) => {
                setErrorForField({key, error: e.message});
                clearSaveState();
            });
    };

    const {toastState: saveToastState} = useManageSaveState(isSaving, 1000);

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();

                setSubmitErrorMessage(null);

                if (props.invalid) {
                    setHttpsValidationFieldErrors();
                    setSubmitErrorMessage(
                        'Some required fields are missing or invalid. Please fix the highlighted fields and try again.'
                    );

                    return;
                }

                return props
                    .handleSubmit()
                    .then((response) => {
                        const hasErrors = handleSubmitErrors(response.data);
                        if (hasErrors) {
                            setSubmitErrorMessage(
                                'Some required fields are missing or invalid. Please fix the highlighted fields and try again.'
                            );
                        }
                    })
                    .catch(() => {
                        setSubmitErrorMessage('Something went wrong. Please try again.');
                    });
            }}
        >
            <fieldset
                disabled={props.isDisabled}
                // remove styling of fieldset - only used for disabling the form
                style={{border: 'unset', margin: 'unset', padding: 'unset'}}
            >
                {props.banner}
                <StackLegacy spacing={24}>
                    <FlashBanner type='info' hasBorder={true} animate={false}>
                        <Body>
                            SMS Campaigns must comply with applicable laws, regulations, Twilio‘s
                            policies, including, but not limited to, the{' '}
                            <Link
                                variant='primary'
                                newTab={true}
                                href='https://www.twilio.com/legal/aup'
                            >
                                Twilio Acceptable Use Policy
                            </Link>{' '}
                            and the{' '}
                            <Link
                                variant='primary'
                                newTab={true}
                                href='https://www.twilio.com/legal/messaging-policy'
                            >
                                Twilio Messaging Policy
                            </Link>
                            , and industry standards, including, but not limited to,
                            telecommunications providers‘ policies. Failure to comply with these
                            policies may result in your campaign being disabled.
                        </Body>
                    </FlashBanner>
                    <Body>
                        This form collects essential information needed to activate text messaging
                        within Nutshell. To ensure a seamless registration, please provide accurate
                        information in the fields below. This information is required by U.S. cell
                        phone carriers to prevent spam and phishing activity.
                    </Body>
                    {props.isLoading ? (
                        <LoadingPage />
                    ) : (
                        <>
                            <div>
                                <div className='mb-16 mt-32'>
                                    <Header>Company information</Header>
                                </div>
                                <div className={styles.fields}>
                                    <Field
                                        key='BUSINESS_NAME'
                                        name='BUSINESS_NAME'
                                        title='Legal business name'
                                        tooltipInfo={
                                            <span>
                                                This name <b>must exactly match</b> your legal
                                                business name as registered with your EIN at the
                                                IRS.
                                            </span>
                                        }
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('BUSINESS_NAME', value);
                                        }}
                                        errorMessage={getErrorMessageForField('BUSINESS_NAME')}
                                        saveToastState={
                                            isSavingKey === 'BUSINESS_NAME' && saveToastState
                                        }
                                        focusOnMount={true}
                                    />
                                    <Field
                                        key='BUSINESS_TYPE'
                                        name='BUSINESS_TYPE'
                                        title='Business type'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormSelectPickerGeneric}
                                        minHeight={TEXTFIELD_HEIGHT}
                                        options={BUSINESS_TYPES}
                                        onChange={(e, value) => {
                                            handleSave('BUSINESS_TYPE', value);
                                        }}
                                        errorMessage={getErrorMessageForField('BUSINESS_TYPE')}
                                        saveToastState={
                                            isSavingKey === 'BUSINESS_TYPE' && saveToastState
                                        }
                                        isDisabled={props.isDisabled}
                                    />
                                    <OwnershipTypeField
                                        handleSave={handleSave}
                                        getErrorMessageForField={getErrorMessageForField}
                                        isSavingKey={isSavingKey}
                                        saveToastState={saveToastState}
                                        isDisabled={props.isDisabled}
                                    />
                                    <Field
                                        key='INDUSTRY'
                                        name='INDUSTRY'
                                        title='Industry'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormSelectPickerGeneric}
                                        minHeight={TEXTFIELD_HEIGHT}
                                        options={INDUSTRIES}
                                        onChange={(e, value) => {
                                            handleSave('INDUSTRY', value);
                                        }}
                                        errorMessage={getErrorMessageForField('INDUSTRY')}
                                        saveToastState={
                                            isSavingKey === 'INDUSTRY' && saveToastState
                                        }
                                        isDisabled={props.isDisabled}
                                    />
                                    <Field
                                        key='GOVERNMENT_REGISTRATION_TYPE'
                                        name='GOVERNMENT_REGISTRATION_TYPE'
                                        title='Government registration type'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormSelectPickerGeneric}
                                        minHeight={TEXTFIELD_HEIGHT}
                                        options={GOVERNMENT_REGISTRATION_TYPES}
                                        tooltipInfo='The country-specific tax identification number for the business'
                                        onChange={(e, value) => {
                                            handleSave('GOVERNMENT_REGISTRATION_TYPE', value);
                                        }}
                                        errorMessage={getErrorMessageForField(
                                            'GOVERNMENT_REGISTRATION_TYPE'
                                        )}
                                        saveToastState={
                                            isSavingKey === 'GOVERNMENT_REGISTRATION_TYPE' &&
                                            saveToastState
                                        }
                                        isDisabled={props.isDisabled}
                                    />
                                    <Field
                                        key='GOVERNMENT_REGISTRATION_NUMBER'
                                        name='GOVERNMENT_REGISTRATION_NUMBER'
                                        title={getGovernmentRegistrationTypeTitle(registrationType)}
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        tooltipInfo='The business tax identification number for your country. In the U.S., this is your EIN, in the following format: 12-3456789.'
                                        onBlur={(e, value) => {
                                            handleSave('GOVERNMENT_REGISTRATION_NUMBER', value);
                                        }}
                                        errorMessage={getErrorMessageForField(
                                            'GOVERNMENT_REGISTRATION_NUMBER'
                                        )}
                                        saveToastState={
                                            isSavingKey === 'GOVERNMENT_REGISTRATION_TYPE' &&
                                            saveToastState
                                        }
                                    />
                                    <Field
                                        key='REGION_OF_OPERATION'
                                        name='REGION_OF_OPERATION'
                                        title='Regions of operation'
                                        isRequired={true}
                                        validate={nonNull}
                                        component={ReduxFormSelectPickerMulti}
                                        options={BUSINESS_REGIONS_OF_OPERATION}
                                        tooltipInfo='The region(s) in which your business operates. Nutshell only supports U.S. and Canadian regions at this time.'
                                        onChange={(e, valueArray) => {
                                            const valueAsString = valueArray
                                                ? // @ts-expect-error
                                                  valueArray.map((val) => val.value).join(',')
                                                : '';

                                            // We're saving the value as a comma separated string
                                            handleSave('REGION_OF_OPERATION', valueAsString);
                                        }}
                                        errorMessage={getErrorMessageForField(
                                            'REGION_OF_OPERATION'
                                        )}
                                        saveToastState={
                                            isSavingKey === 'REGION_OF_OPERATION' && saveToastState
                                        }
                                        isDisabled={props.isDisabled}
                                    />
                                </div>
                                <div className={styles.fields}>
                                    <Field
                                        key='STREET_ADDRESS'
                                        name='STREET_ADDRESS'
                                        title='Street address'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('STREET_ADDRESS', value);
                                        }}
                                        errorMessage={getErrorMessageForField('STREET_ADDRESS')}
                                        saveToastState={
                                            isSavingKey === 'STREET_ADDRESS' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='STREET_ADDRESS_2'
                                        name='STREET_ADDRESS_2'
                                        title='Street address 2 (optional)'
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('STREET_ADDRESS_2', value);
                                        }}
                                        errorMessage={getErrorMessageForField('STREET_ADDRESS_2')}
                                        saveToastState={
                                            isSavingKey === 'STREET_ADDRESS_2' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='CITY'
                                        name='CITY'
                                        title='City'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('CITY', value);
                                        }}
                                        errorMessage={getErrorMessageForField('CITY')}
                                        saveToastState={isSavingKey === 'CITY' && saveToastState}
                                    />
                                    <Field
                                        key='REGION'
                                        name='REGION'
                                        title='Region'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormSelectPickerGeneric}
                                        minHeight={TEXTFIELD_HEIGHT}
                                        options={REGIONS}
                                        onChange={(e, value) => {
                                            handleSave('REGION', value);
                                        }}
                                        errorMessage={getErrorMessageForField('REGION')}
                                        saveToastState={isSavingKey === 'REGION' && saveToastState}
                                        isDisabled={props.isDisabled}
                                    />
                                    <Field
                                        key='POSTAL_CODE'
                                        name='POSTAL_CODE'
                                        title='Postal code'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('POSTAL_CODE', value);
                                        }}
                                        errorMessage={getErrorMessageForField('POSTAL_CODE')}
                                        saveToastState={
                                            isSavingKey === 'POSTAL_CODE' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='COUNTRY'
                                        name='COUNTRY'
                                        title='Country'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormSelectPickerGeneric}
                                        minHeight={TEXTFIELD_HEIGHT}
                                        options={COUNTRIES}
                                        onChange={(e, value) => {
                                            handleSave('COUNTRY', value);
                                        }}
                                        errorMessage={getErrorMessageForField('COUNTRY')}
                                        saveToastState={
                                            isSavingKey === 'GOVERNMENT_REGISTRATION_TYPE' &&
                                            saveToastState
                                        }
                                        isDisabled={props.isDisabled}
                                    />
                                    <Field
                                        key='SOCIAL_URL'
                                        name='SOCIAL_URL'
                                        title='Social URL'
                                        placeholder='https://'
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            const hasProtocol =
                                                value.startsWith('https://') ||
                                                value.startsWith('http://');

                                            const emptyValue = value.length === 0;

                                            handleSave(
                                                'SOCIAL_URL',
                                                hasProtocol || emptyValue
                                                    ? value
                                                    : `http://${value}`
                                            );
                                        }}
                                        errorMessage={getErrorMessageForField('SOCIAL_URL')}
                                        saveToastState={
                                            isSavingKey === 'SOCIAL_URL' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='WEBSITE_URL'
                                        name='WEBSITE_URL'
                                        title='Website URL'
                                        placeholder='https://'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        tooltipInfo='The URL for the website of the business'
                                        onBlur={(e, value) => {
                                            const hasProtocol =
                                                value.startsWith('https://') ||
                                                value.startsWith('http://');

                                            const emptyValue = value.length === 0;

                                            handleSave(
                                                'WEBSITE_URL',
                                                hasProtocol || emptyValue
                                                    ? value
                                                    : `http://${value}`
                                            );
                                        }}
                                        errorMessage={getErrorMessageForField('WEBSITE_URL')}
                                        saveToastState={
                                            isSavingKey === 'WEBSITE_URL' && saveToastState
                                        }
                                    />
                                </div>
                            </div>
                            <div>
                                <div className='my-32'>
                                    <div className='mb-4'>
                                        <Header>Business representative information</Header>
                                    </div>
                                    <Body as='div' isItalic={true} color='light'>
                                        This individual will serve as the point of contact for text
                                        messaging activities, overseeing accountability, quality
                                        assurance, and adherence to legal standards.
                                    </Body>
                                </div>
                                <div className={styles.fields}>
                                    <Field
                                        key='REP_FIRST_NAME'
                                        name='REP_FIRST_NAME'
                                        title='First name'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('REP_FIRST_NAME', value);
                                        }}
                                        errorMessage={getErrorMessageForField('REP_FIRST_NAME')}
                                        saveToastState={
                                            isSavingKey === 'REP_FIRST_NAME' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='REP_LAST_NAME'
                                        name='REP_LAST_NAME'
                                        title='Last name'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('REP_LAST_NAME', value);
                                        }}
                                        errorMessage={getErrorMessageForField('REP_LAST_NAME')}
                                        saveToastState={
                                            isSavingKey === 'REP_LAST_NAME' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='REP_PHONE_NUMBER'
                                        name='REP_PHONE_NUMBER'
                                        title='Phone #'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('REP_PHONE_NUMBER', value);
                                        }}
                                        errorMessage={getErrorMessageForField('REP_PHONE_NUMBER')}
                                        saveToastState={
                                            isSavingKey === 'REP_PHONE_NUMBER' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='REP_EMAIL'
                                        name='REP_EMAIL'
                                        title='Email'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('REP_EMAIL', value);
                                        }}
                                        errorMessage={getErrorMessageForField('REP_EMAIL')}
                                        saveToastState={
                                            isSavingKey === 'REP_EMAIL' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='REP_TITLE'
                                        name='REP_TITLE'
                                        title='Title'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormTextField}
                                        onBlur={(e, value) => {
                                            handleSave('REP_TITLE', value);
                                        }}
                                        errorMessage={getErrorMessageForField('REP_TITLE')}
                                        saveToastState={
                                            isSavingKey === 'REP_TITLE' && saveToastState
                                        }
                                    />
                                    <Field
                                        key='REP_POSITION'
                                        name='REP_POSITION'
                                        title='Position'
                                        isRequired={true}
                                        validate={[nonNull]}
                                        component={ReduxFormSelectPickerGeneric}
                                        minHeight={TEXTFIELD_HEIGHT}
                                        options={POSITIONS}
                                        onChange={(e, value) => {
                                            handleSave('REP_POSITION', value);
                                        }}
                                        errorMessage={getErrorMessageForField('REP_POSITION')}
                                        saveToastState={
                                            isSavingKey === 'REP_POSITION' && saveToastState
                                        }
                                        isDisabled={props.isDisabled}
                                        focusOnMount={false}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className='my-32'>
                                    <div className='mb-4'>
                                        <Header>Campaign</Header>
                                    </div>
                                    <Body as='div' isItalic={true} color='light'>
                                        Set up your campaign to send weekly marketing messages about
                                        sales and offers from your company to end customers who have
                                        opted in.
                                    </Body>
                                </div>
                                <Field
                                    key='SMS_PLANS_DESCRIPTION'
                                    name='SMS_PLANS_DESCRIPTION'
                                    title='Description of your text plans'
                                    subtitle='Include who the sender is, who your recipients are, and why messages are being sent. For example: "We will send a welcome message and a discount offers from Acme Steamroller Company to customers who have opted in. Our team will interact with them via text to answer questions on business requirements."'
                                    isRequired={true}
                                    validate={[nonNull]}
                                    component={ReduxFormTextField}
                                    multiline={true}
                                    onBlur={(e, value) => {
                                        handleSave('SMS_PLANS_DESCRIPTION', value);
                                    }}
                                    errorMessage={getErrorMessageForField('SMS_PLANS_DESCRIPTION')}
                                    saveToastState={
                                        isSavingKey === 'SMS_PLANS_DESCRIPTION' && saveToastState
                                    }
                                />
                                <Field
                                    key='OPT_IN_DESCRIPTION'
                                    name='OPT_IN_DESCRIPTION'
                                    title='Opt-in description'
                                    subtitle={
                                        <div>
                                            Describe how recipients opt in to receiving messages. If
                                            multiple opt-in methods are used, include all of them.
                                            You must include a link to your privacy policy & terms
                                            of service. Your privacy policy must include a statement
                                            of non-sharing for mobile numbers, message frequency,
                                            and a &quot;message and data rates apply&quot;
                                            disclosure.
                                            <br />
                                            <br />
                                            For example &quot;End users opt-in by visiting
                                            www.example.com and adding their phone number. They then
                                            check a box agreeing to receive text messages from Acme,
                                            Inc. Additionally, end users can also opt-in by texting
                                            START to (111) 555-3333 to opt in. Terms and Conditions
                                            at www.example.com/tc. Privacy Policy at
                                            www.example.com/privacy&quot;
                                        </div>
                                    }
                                    isRequired={true}
                                    validate={[nonNull]}
                                    component={ReduxFormTextField}
                                    multiline={true}
                                    onBlur={(e, value) => {
                                        handleSave('OPT_IN_DESCRIPTION', value);
                                    }}
                                    errorMessage={getErrorMessageForField('OPT_IN_DESCRIPTION')}
                                    saveToastState={
                                        isSavingKey === 'OPT_IN_DESCRIPTION' && saveToastState
                                    }
                                />
                                <Field
                                    key='PRIVACY_POLICY_URL'
                                    name='PRIVACY_POLICY_URL'
                                    title='Privacy Policy URL'
                                    placeholder='https://'
                                    isRequired={true}
                                    validate={[nonNull, validateHttpsUrl]}
                                    component={ReduxFormTextField}
                                    onBlur={(e, value) => {
                                        handleSave('PRIVACY_POLICY_URL', value);
                                    }}
                                    errorMessage={getErrorMessageForField('PRIVACY_POLICY_URL')}
                                    saveToastState={
                                        isSavingKey === 'PRIVACY_POLICY_URL' && saveToastState
                                    }
                                />
                                <Field
                                    key='TERMS_AND_CONDITIONS_URL'
                                    name='TERMS_AND_CONDITIONS_URL'
                                    title='Terms & Conditions URL'
                                    placeholder='https://'
                                    isRequired={true}
                                    validate={[nonNull, validateHttpsUrl]}
                                    component={ReduxFormTextField}
                                    onBlur={(e, value) => {
                                        handleSave('TERMS_AND_CONDITIONS_URL', value);
                                    }}
                                    errorMessage={getErrorMessageForField(
                                        'TERMS_AND_CONDITIONS_URL'
                                    )}
                                    saveToastState={
                                        isSavingKey === 'TERMS_AND_CONDITIONS_URL' && saveToastState
                                    }
                                />
                                <Field
                                    key='SAMPLE_MESSAGE_1'
                                    name='SAMPLE_MESSAGE_1'
                                    title='Sample message #1'
                                    subtitle={
                                        <div>
                                            Example: &quot;Hi! This is Andy from Nutshell. I wanted
                                            to see if I could answer any questions about your
                                            inquiry for a new warehouse roof. If you’d like to opt
                                            out of any other messages, just text STOP in reply to
                                            this message.&quot;
                                        </div>
                                    }
                                    isRequired={true}
                                    validate={[nonNull]}
                                    component={ReduxFormTextField}
                                    multiline={true}
                                    onBlur={(e, value) => {
                                        handleSave('SAMPLE_MESSAGE_1', value);
                                    }}
                                    errorMessage={getErrorMessageForField('SAMPLE_MESSAGE_1')}
                                    saveToastState={
                                        isSavingKey === 'SAMPLE_MESSAGE_1' && saveToastState
                                    }
                                />
                                <Field
                                    key='SAMPLE_MESSAGE_2'
                                    name='SAMPLE_MESSAGE_2'
                                    title='Sample message #2'
                                    isRequired={true}
                                    validate={[nonNull]}
                                    component={ReduxFormTextField}
                                    multiline={true}
                                    onBlur={(e, value) => {
                                        handleSave('SAMPLE_MESSAGE_2', value);
                                    }}
                                    errorMessage={getErrorMessageForField('SAMPLE_MESSAGE_2')}
                                    saveToastState={
                                        isSavingKey === 'SAMPLE_MESSAGE_2' && saveToastState
                                    }
                                />
                                <Field
                                    key='LINKS_INCLUDED'
                                    name='LINKS_INCLUDED'
                                    title='Will you include any links in your messages?'
                                    subtitle='When possible, include any links in your sample messages.'
                                    isRequired={true}
                                    validate={[nonNull]}
                                    component={ReduxFormRadioGroup}
                                    options={YES_NO_OPTIONS}
                                    onChange={(e, value) => {
                                        handleSave('LINKS_INCLUDED', value);
                                    }}
                                    errorMessage={getErrorMessageForField('LINKS_INCLUDED')}
                                    saveToastState={
                                        isSavingKey === 'LINKS_INCLUDED' && saveToastState
                                    }
                                />
                                <Field
                                    key='PHONE_NUMBERS_INCLUDED'
                                    name='PHONE_NUMBERS_INCLUDED'
                                    title='Will you include any phone numbers in your messages?'
                                    subtitle='When possible, include any numbers in your sample messages.'
                                    isRequired={true}
                                    validate={[nonNull]}
                                    component={ReduxFormRadioGroup}
                                    options={YES_NO_OPTIONS}
                                    onChange={(e, value) => {
                                        handleSave('PHONE_NUMBERS_INCLUDED', value);
                                    }}
                                    errorMessage={getErrorMessageForField('PHONE_NUMBERS_INCLUDED')}
                                    saveToastState={
                                        isSavingKey === 'PHONE_NUMBERS_INCLUDED' && saveToastState
                                    }
                                />
                                <Field
                                    key='LENDING_INFO_INCLUDED'
                                    name='LENDING_INFO_INCLUDED'
                                    title='Will you include any content related to lending or other loan arrangements in your messages?'
                                    isRequired={true}
                                    validate={[nonNull]}
                                    component={ReduxFormRadioGroup}
                                    options={YES_NO_OPTIONS}
                                    onChange={(e, value) => {
                                        handleSave('LENDING_INFO_INCLUDED', value);
                                    }}
                                    errorMessage={getErrorMessageForField('LENDING_INFO_INCLUDED')}
                                    saveToastState={
                                        isSavingKey === 'LENDING_INFO_INCLUDED' && saveToastState
                                    }
                                />
                                <Field
                                    key='AGE_GATED_CONTENT_INCLUDED'
                                    name='AGE_GATED_CONTENT_INCLUDED'
                                    title={
                                        <div>
                                            Will you include any age-gated content as defined in{' '}
                                            <Link
                                                variant='primary'
                                                newTab={true}
                                                href='https://www.ctia.org/the-wireless-industry/industry-commitments/messaging-interoperability-sms-mms'
                                            >
                                                Carrier and CTIA guidelines
                                            </Link>{' '}
                                            in your messages?
                                        </div>
                                    }
                                    isRequired={true}
                                    validate={[nonNull]}
                                    component={ReduxFormRadioGroup}
                                    options={YES_NO_OPTIONS}
                                    onChange={(e, value) => {
                                        handleSave('AGE_GATED_CONTENT_INCLUDED', value);
                                    }}
                                    errorMessage={getErrorMessageForField(
                                        'AGE_GATED_CONTENT_INCLUDED'
                                    )}
                                    saveToastState={
                                        isSavingKey === 'AGE_GATED_CONTENT_INCLUDED' &&
                                        saveToastState
                                    }
                                />
                            </div>
                            <div className={styles.footer}>
                                {submitErrorMessage && (
                                    <Body size='small' color='error'>
                                        {submitErrorMessage}
                                    </Body>
                                )}
                                <SaveButton
                                    isSaving={props.submitting}
                                    // @ts-expect-error
                                    type='submit'
                                    buttonText='Submit'
                                    savingText='Submitting…'
                                    successText='Submitted!'
                                    disabled={props.isDisabled}
                                    initialSaveState={props.isDisabled ? 'complete' : undefined}
                                />
                            </div>
                        </>
                    )}
                </StackLegacy>
            </fieldset>
        </form>
    );
}
